'use client';
import Link from 'next/link';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/navigation';
import { isRelativeURL } from '@/components/utils/urlUtils';

// ─── Singleton portal container ──────────────────────────────────────────────
// One DOM node + one resize listener shared across all LinkNoPrefetch instances.
// globals.css applies `zoom: 0.9` on :root at ≥768px which creates a new
// containing block for fixed-position descendants. We append a sibling div
// directly to body (outside the zoom context) and size it via the --root-zoom
// custom property so it covers the actual viewport.
let _portalEl = null;
let _refCount = 0;

function _applyPortalSize() {
    if (!_portalEl) return;
    const rootZoom =
        parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--root-zoom')) || 1;
    Object.assign(_portalEl.style, {
        position: 'fixed',
        top: '0',
        left: '0',
        width: `${100 / rootZoom}vw`,
        height: `${100 / rootZoom}vh`,
        zIndex: '99999',
        pointerEvents: 'none'
    });
}

function _acquirePortal() {
    if (_refCount === 0) {
        _portalEl = document.createElement('div');
        _applyPortalSize();
        window.addEventListener('resize', _applyPortalSize);
        document.body.appendChild(_portalEl);
    }
    _refCount++;
    return _portalEl;
}

function _releasePortal() {
    _refCount--;
    if (_refCount === 0 && _portalEl) {
        window.removeEventListener('resize', _applyPortalSize);
        if (document.body.contains(_portalEl)) document.body.removeChild(_portalEl);
        _portalEl = null;
    }
}
// ─────────────────────────────────────────────────────────────────────────────

function LinkNoPrefetch({ disableClickEffect = false, ...props }) {
    const [isClicked, setIsClicked] = useState(false);
    const [portalEl, setPortalEl] = useState(null);
    const router = useRouter();

    useEffect(() => {
        const el = _acquirePortal();
        setPortalEl(el);
        return () => {
            _releasePortal();
            setPortalEl(null);
        };
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        if (!isClicked || disableClickEffect) return;

        const timeout = setTimeout(() => {
            setIsClicked(false);
        }, 30_000);

        return () => clearTimeout(timeout);
    }, [isClicked, disableClickEffect]);

    const handleClick = (e) => {
        if (typeof window === 'undefined') {
            return;
        }

        const href = props.href;

        if (href === '#') {
            e.preventDefault();
            return;
        }

        // Check for modifier keys (Ctrl, Cmd, Shift) or non-left clicks
        const isModifiedClick = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;

        // Check if link has a target attribute (like target="_blank")
        const hasTarget = props.target && props.target !== '_self';

        // Check if this is an external URL (different origin from the current page)
        let isExternal = false;
        try {
            const url = new URL(href, window.location.origin);
            isExternal = url.origin !== window.location.origin;
        } catch {
            // If URL parsing fails, fall back to treating the link as internal
            isExternal = false;
        }

        // For external URLs with modifiers or target attributes, let the browser handle it naturally
        if (isExternal && (isModifiedClick || hasTarget)) {
            // Don't prevent default - let browser handle new tab/window behavior
            props.onClick?.(e);
            return;
        }

        // For simple external links (no modifiers, no special target), use same-tab navigation
        if (isExternal) {
            e.preventDefault();
            // Validate scheme and use native navigation for safe protocols only
            try {
                const url = new URL(href, window.location.href);
                const protocol = url.protocol.toLowerCase();
                const isHttp = protocol === 'http:' || protocol === 'https:';
                const isMailOrTel = protocol === 'mailto:' || protocol === 'tel:';
                if (isHttp || isMailOrTel) {
                    window.location.href = href;
                    props.onClick?.(e);
                }
                // Silently ignore unsafe protocols (javascript:, data:, etc.)
            } catch {
                // If URL parsing fails, do not navigate
            }
            return;
        }

        // For internal links with modifiers or target attributes, let the browser handle it
        if (isModifiedClick || hasTarget) {
            // Don't prevent default - let browser handle new tab/window behavior
            props.onClick?.(e);
            return;
        }

        // For simple internal links, prevent default and use Next.js router
        e.preventDefault();

        const currentPath = window.location.pathname;
        let hrefPath;
        if (isRelativeURL(href)) {
            hrefPath = href;
        } else {
            try {
                hrefPath = new URL(href).pathname;
            } catch {
                hrefPath = href;
            }
        }
        if (hrefPath === currentPath) {
            window.location.reload();
            return;
        }

        if (!disableClickEffect) {
            setIsClicked(true);
        }

        window.history.pushState({ path: currentPath }, '', currentPath);

        router.push(href);

        props.onClick?.(e);
    };

    useEffect(() => {
        const handlePopState = (event) => {
            if (event.state?.path) {
                router.push(event.state.path);
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [router]);

    const overlay =
        isClicked && !disableClickEffect && portalEl
            ? createPortal(
                  <div className="flex size-full items-center justify-center bg-gray-900/40 backdrop-blur-sm [pointer-events:auto]">
                      <div className="relative flex size-24 items-center justify-center">
                          <div className="absolute size-24 animate-ping rounded-full bg-fuchsia-500/30 blur-2xl" />
                          <div className="relative size-10 animate-pulse rounded-full bg-fuchsia-600" />
                      </div>
                  </div>,
                  portalEl
              )
            : null;

    return (
        <>
            <Link prefetch={false} {...props} onClick={handleClick}>
                {props.children}
            </Link>

            {overlay}
        </>
    );
}

export default LinkNoPrefetch;
